import { AppConfiguration } from "config";
import { genericGetRequest, genericPostRequest, genericPostRequestFeatures } from "helpers/sf_backend_helper";
import { bsh_getAdminUserDetails, bsh_getInsuranceIoUserDetails } from "helpers/sf_browser_storage_helper";
import { call, put, select, takeEvery } from "redux-saga/effects";
import {
  getAggrGroupByTagsDataSuccess,
  getAggrTagsDataError,
  getAggrTagsDataSuccess,
  getApiDataByIdError,
  getApiDataByIdSuccess,
  getApiTimingsDataError,
  getApiTimingsDataSuccess,
  getProfilingTagsError,
  getProfilingTagsSuccess,
  getDmsLogsStreamsSuccess,
  getDmsLogsStreamsError,
  getDmsStatsDataError,
  getDmsStatsDataSuccess,
  getDmsLogsDataError,
  getDmsLogsDataSuccess,
  getRuleNamesSuccess,
  getRuleNamesError,
  getProductsNamesSuccess,
  getProductsNamesError,
  getRuleLogsSuccess,
  getRuleLogsError,
  getRuleStatsSuccess,
  getRuleStatsError,
  getAccountsListSuccess,
  getAccountsListError,
  getRuleLogDetailsSuccess,
  getRuleLogDetailsError,
  exportApiTimingsDataSuccess,
  exportApiTimingsDataError,
} from "./actions";
import { buildApiTimingsCacheKey } from "helpers/campaign_fulfillment_pagination_helper";
import {
  GET_AGGR_TAGS_DATA,
  GET_API_DATA_BY_ID,
  GET_API_TIMINGS_DATA,
  GET_PROFILING_TAGS,
  GET_DMS_LOGS_STREAMS,
  GET_DMS_LOGS_DATA,
  GET_DMS_STATS_DATA,
  GET_RULE_NAMES,
  GET_ACCOUNTS_LIST,
  GET_PRODUCTS_NAMES,
  GET_RULE_LOGS,
  GET_RULE_STATS,
  GET_RULE_LOG_DETAILS,
  EXPORT_API_TIMINGS_DATA,
} from "./actionTypes";
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert";
import { showExportEnqueuedToast } from "helpers/export_toast_helper";

function* onGetProfilingTags() {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/profiling_tags`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getProfilingTagsSuccess(response))
  } catch (error) {
    yield put(getProfilingTagsError(error))
  }
}

function* onGetAggrTagsData(action) {
  try {
    let user = bsh_getAdminUserDetails();
    let master_user = bsh_getInsuranceIoUserDetails();
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/get_api_timing_stats`,
      {
        body: JSON.stringify(action.payload),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token,
          "is-master": action?.payload?.is_master,
          "master-token": master_user?.token
        }
      }
    )
    if (action.payload?.group_by_tag == "1") {
      yield put(getAggrGroupByTagsDataSuccess(response))
    } else {
      yield put(getAggrTagsDataSuccess(response))
    }
  } catch (error) {
    yield put(getAggrTagsDataError(error))
  }
}

const inFlightApiTimingsRequests = new Set()

function* onGetApiTimingData(action) {
  try {
    let user = bsh_getAdminUserDetails();
    let master_user = bsh_getInsuranceIoUserDetails();
    const queryParams = action?.payload?.queryParams || action?.payload || {}
    const useCache = Boolean(action.payload?.useCache)
    const requestedPageNumber = parseInt(queryParams?.page_number, 10) || 1
    const cacheKey = useCache ? buildApiTimingsCacheKey(queryParams) : null
    const requestKey = `${cacheKey}:${requestedPageNumber}`
    const cachedPage = useCache
      ? yield select((state) => state.DevDashboard.page_cache?.[cacheKey]?.[requestedPageNumber])
      : null

    if (useCache && cachedPage) {
      return
    }

    if (useCache && inFlightApiTimingsRequests.has(requestKey)) {
      return
    }

    if (useCache) {
      inFlightApiTimingsRequests.add(requestKey)
    }

    const response = yield call(
      genericPostRequestFeatures,
      `${AppConfiguration.BACKEND_URL}/api/v1/api_timing_data`,
      {
        body: JSON.stringify(queryParams),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token,
          "is-master": queryParams?.is_master,
          "master-token": master_user?.token
        }
      }
    )
    yield put(getApiTimingsDataSuccess(response, {
      queryParams,
      appendResults: Boolean(action.payload?.appendResults),
      useCache,
    }))
  } catch (error) {
    yield put(getApiTimingsDataError(error))
  } finally {
    if (action.payload?.useCache) {
      const queryParams = action?.payload?.queryParams || action?.payload || {}
      const requestedPageNumber = parseInt(queryParams?.page_number, 10) || 1
      const cacheKey = buildApiTimingsCacheKey(queryParams)
      inFlightApiTimingsRequests.delete(`${cacheKey}:${requestedPageNumber}`)
    }
  }
}

function* onGetApiTimingDataById(action) {
  try {
    let user = bsh_getAdminUserDetails();
    let master_user = bsh_getInsuranceIoUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/api_timing_data/${action.payload?.id}`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token,
        "is-master": action?.payload?.is_master,
        "master-token": master_user?.token
      })
    yield put(getApiDataByIdSuccess(response))
  } catch (error) {
    yield put(getApiDataByIdError(error))
  }
}

function* onExportApiTimingData(action) {
  try {
    let user = bsh_getAdminUserDetails();
    let master_user = bsh_getInsuranceIoUserDetails();
    const queryParams = action?.payload?.queryParams || {}
    yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/api_timing_data/export`,
      {
        body: JSON.stringify(queryParams),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token,
          "is-master": queryParams?.is_master,
          "master-token": master_user?.token
        }
      }
    )
    yield put(exportApiTimingsDataSuccess())
    showExportEnqueuedToast({ reportExportsAvailable: true })
  } catch (error) {
    yield put(exportApiTimingsDataError(error))
    ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: "Failed to queue API timings export." })
  }
}

//DMS specific

function* onGetDmsLogsStreams(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/dms_logs/log_streams`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getDmsLogsStreamsSuccess(response))
  } catch (error) {
    yield put(getDmsLogsStreamsError(error))
  }
}

function* onGetDmsStatsData(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/dms_logs/stats`,
      {
        body: JSON.stringify(action.payload),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token
        }
      }
    )
    yield put(getDmsStatsDataSuccess(response))
  } catch (error) {
    yield put(getDmsStatsDataError(error))
  }
}

function* onGetDmsLogsData(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericPostRequestFeatures,
      `${AppConfiguration.BACKEND_URL}/api/v1/dms_logs`,
      {
        body: JSON.stringify(action.payload),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token
        }
      }
    )
    yield put(getDmsLogsDataSuccess(response))
  } catch (error) {
    yield put(getDmsLogsDataError(error))
  }
}

// RULES VALIDATION 

function* onGetRuleNames(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/rule_validator_logs/rule_names`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getRuleNamesSuccess(response))
  } catch (error) {
    yield put(getRuleNamesError(error))
  }
}

function* onGetAccounts(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_account_list`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getAccountsListSuccess(response))
  } catch (error) {
    yield put(getAccountsListError(error))
  }
}

function* onGetProductNames(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/rule_validator_logs/product_names`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getProductsNamesSuccess(response))
  } catch (error) {
    yield put(getProductsNamesError(error))
  }
}

function* onGetRuleLogs(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericPostRequestFeatures,
      `${AppConfiguration.BACKEND_URL}/api/v1/rule_validator_logs`,
      {
        body: JSON.stringify(action.payload),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token
        }
      }
    )
    yield put(getRuleLogsSuccess(response))
  } catch (error) {
    yield put(getRuleLogsError(error))
  }
}

function* onGetRulesStats(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/rule_validator_logs/stats`,
      {
        body: JSON.stringify(action.payload),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token
        }
      }
    )
    yield put(getRuleStatsSuccess(response))
  } catch (error) {
    yield put(getRuleStatsError(error))
  }
}

function* onGetRuleLogsDetails(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/rule_validator_logs/${action.payload?.id}/error`,
      {},
      {
        "Content-Type": "application/json",
        "Authorization": user.token
      })
    yield put(getRuleLogDetailsSuccess(response))
  } catch (error) {
    yield put(getRuleLogDetailsError(error))
  }
}


function* DevDashboardSaga() {
  yield takeEvery(GET_PROFILING_TAGS, onGetProfilingTags)
  yield takeEvery(GET_AGGR_TAGS_DATA, onGetAggrTagsData)
  yield takeEvery(GET_API_TIMINGS_DATA, onGetApiTimingData)
  yield takeEvery(EXPORT_API_TIMINGS_DATA, onExportApiTimingData)
  yield takeEvery(GET_API_DATA_BY_ID, onGetApiTimingDataById)
  yield takeEvery(GET_DMS_LOGS_STREAMS, onGetDmsLogsStreams)
  yield takeEvery(GET_DMS_STATS_DATA, onGetDmsStatsData)
  yield takeEvery(GET_DMS_LOGS_DATA, onGetDmsLogsData)
  yield takeEvery(GET_RULE_NAMES, onGetRuleNames)
  yield takeEvery(GET_ACCOUNTS_LIST, onGetAccounts)
  yield takeEvery(GET_PRODUCTS_NAMES, onGetProductNames)
  yield takeEvery(GET_RULE_LOGS, onGetRuleLogs)
  yield takeEvery(GET_RULE_STATS, onGetRulesStats)
  yield takeEvery(GET_RULE_LOG_DETAILS, onGetRuleLogsDetails)
}

export default DevDashboardSaga;
